import { PRACTITIONER_TEST_PRODUCT_CONNECTION_FIELDS } from "@/graphql/shop";
import { gql } from "@apollo/client";

export const SUPPORTING_DOCUMENT_FIELDS = gql`
  fragment SupportingDocumentFields on SupportingDocumentType {
    id
    title
    displayName
    url
    contentType
    description
    filename
    type
    testProductNames
  }
`;

export const VIDEO_FIELDS = gql`
  fragment VideoFields on VideoType {
    id
    url
    thumbnailUrl
    thumbnailOverrideUrl
    userStarted
    userEnded
  }
`;

export const VIDEO_GUIDE_FIELDS = gql`
  fragment VideoGuideFields on VideoGuideType {
    id
    video {
      ...VideoFields
    }
    title
    shortDescription
    topic {
      id
      name
    }
    description
    slug
  }
  ${VIDEO_FIELDS}
`;

export const COURSE_VIDEO_FIELDS = gql`
  fragment CourseVideoFields on CourseVideoType {
    id
    title
    video {
      ...VideoFields
    }
  }
  ${VIDEO_FIELDS}
`;

export const COURSE_MODULE_FIELDS = gql`
  fragment CourseModuleFields on CourseModuleType {
    id
    title
    videos {
      ...CourseVideoFields
    }
  }
  ${COURSE_VIDEO_FIELDS}
`;

export const BADGE_FIELDS = gql`
  fragment BadgeFields on BadgeType {
    id
    name
  }
`;

export const COURSE_LIST_FIELDS = gql`
  fragment CourseListFields on CourseType {
    id
    title
    subtitle
    slug
    shortDescription
    imageUrl
    backgroundColor
    courseBadges {
      id
      badge {
        ...BadgeFields
      }
      points
    }
  }
  ${BADGE_FIELDS}
`;

export const WEBINAR_LIST_HOST_FIELDS = gql`
  fragment WebinarListHostFields on HostType {
    id
    firstName
    lastName
    fullName
    avatarUrl
    title
    occupation
  }
`;

export const WEBINAR_BADGE_FIELDS = gql`
  fragment WebinarBadgeFields on WebinarBadgeType {
    id
    badge {
      ...BadgeFields
    }
    points
  }
  ${BADGE_FIELDS}
`;

export const WEBINAR_LIST_FIELDS = gql`
  fragment WebinarListFields on WebinarType {
    id
    title
    slug
    shortDescription
    startTime
    endTime
    webinarBadges {
      ...WebinarBadgeFields
    }
    host {
      ...WebinarListHostFields
    }
  }
  ${WEBINAR_BADGE_FIELDS}
  ${WEBINAR_LIST_HOST_FIELDS}
`;

export const COURSE_DETAIL_FIELDS = gql`
  fragment CourseDetailFields on CourseType {
    id
    title
    subtitle
    slug
    description
    shortDescription
    hasExclusiveAccess
    exclusiveAccessStartTime
    exclusiveAccessEndTime
    modules {
      ...CourseModuleFields
    }
    documents {
      ...SupportingDocumentFields
    }
    courseBadges {
      id
      badge {
        ...BadgeFields
      }
      points
    }
    imageUrl
    backgroundColor
    offerText
    offerCaveat
    discountCode {
      id
      code
    }
    introductionVideo {
      ...VideoFields
    }
  }
  ${BADGE_FIELDS}
  ${VIDEO_FIELDS}
  ${COURSE_MODULE_FIELDS}
  ${SUPPORTING_DOCUMENT_FIELDS}
`;

export const COURSE_FIELDS = gql`
  fragment CourseFields on CourseType {
    ...CourseDetailFields
    testProducts(paymentIsoCode: $paymentIsoCode, organisation: $organisation) {
      ...PractitionerTestProductConnectionFields
      laboratory {
        id
        name
        logoUrl
      }
    }
  }
  ${COURSE_DETAIL_FIELDS}
  ${PRACTITIONER_TEST_PRODUCT_CONNECTION_FIELDS}
`;

export const WEBINAR_DETAIL_FIELDS = gql`
  fragment WebinarDetailFields on WebinarType {
    id
    title
    slug
    shortDescription
    description
    startTime
    endTime
    registerNowUrl
    webinarBadges {
      ...WebinarBadgeFields
    }
    video {
      ...VideoFields
    }
    host {
      ...WebinarListHostFields
    }
    documents {
      ...SupportingDocumentFields
    }
  }
  ${WEBINAR_BADGE_FIELDS}
  ${WEBINAR_LIST_HOST_FIELDS}
  ${SUPPORTING_DOCUMENT_FIELDS}
  ${VIDEO_FIELDS}
`;

export const WEBINAR_FIELDS = gql`
  fragment WebinarFields on WebinarType {
    ...WebinarDetailFields
    testProducts(paymentIsoCode: $paymentIsoCode, organisation: $organisation) {
      ...PractitionerTestProductConnectionFields
      laboratory {
        id
        name
        logoUrl
      }
    }
  }
  ${WEBINAR_DETAIL_FIELDS}
  ${PRACTITIONER_TEST_PRODUCT_CONNECTION_FIELDS}
`;

export const EVENT_FIELDS = gql`
  fragment EventFields on EventType {
    id
    title
    startTime
    endTime
    description
    shortDescription
    location
    type
    slug
    imageUrl
    registerNowUrl
    eventBadges {
      id
      badge {
        ...BadgeFields
      }
      points
    }
  }
  ${BADGE_FIELDS}
`;

export const EVENT_QUERY = gql`
  query EventQuery($id: ID, $slug: String) {
    event(id: $id, slug: $slug) {
      ...EventFields
    }
  }
  ${EVENT_FIELDS}
`;

export const COURSE_QUERY = gql`
  query CourseQuery($id: ID, $slug: String) {
    course(id: $id, slug: $slug) {
      ...CourseDetailFields
    }
  }
  ${COURSE_DETAIL_FIELDS}
`;

export const COURSE_TEST_PRODUCTS_QUERY = gql`
  query CourseTestProductsQuery($slug: String!, $paymentIsoCode: String, $organisation: ID) {
    course(slug: $slug) {
      id
      testProducts(paymentIsoCode: $paymentIsoCode, organisation: $organisation) {
        ...PractitionerTestProductConnectionFields
        laboratory {
          id
          name
          logoUrl
        }
      }
    }
  }
  ${PRACTITIONER_TEST_PRODUCT_CONNECTION_FIELDS}
`;

export const WEBINAR_QUERY = gql`
  query WebinarQuery($id: ID, $slug: String) {
    webinar(id: $id, slug: $slug) {
      ...WebinarDetailFields
    }
  }
  ${WEBINAR_DETAIL_FIELDS}
`;

export const WEBINAR_TEST_PRODUCTS_QUERY = gql`
  query WebinarTestProductsQuery($slug: String!, $paymentIsoCode: String, $organisation: ID) {
    webinar(slug: $slug) {
      id
      testProducts(paymentIsoCode: $paymentIsoCode, organisation: $organisation) {
        ...PractitionerTestProductConnectionFields
        laboratory {
          id
          name
          logoUrl
        }
      }
    }
  }
  ${PRACTITIONER_TEST_PRODUCT_CONNECTION_FIELDS}
`;

export const COURSE_CONNECTIONS_QUERY = gql`
  query CourseConnectionsQuery(
    $first: Int!
    $after: String
    $orderBy: String
    $search: String
    $testingServices: String
    $healthAreas: String
    $sampleTypes: String
  ) {
    courseConnections(
      first: $first
      after: $after
      orderBy: $orderBy
      search: $search
      testingServices: $testingServices
      healthAreas: $healthAreas
      sampleTypes: $sampleTypes
    ) {
      edges {
        node {
          ...CourseListFields
        }
        cursor
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
  ${COURSE_LIST_FIELDS}
`;

export const HOMEPAGE_QUERY = gql`
  query HomepageQuery {
    learningHubHomepage {
      webinars {
        ...WebinarListFields
      }
      courses {
        ...CourseListFields
      }
      events {
        ...EventFields
      }
    }
  }
  ${COURSE_LIST_FIELDS}
  ${EVENT_FIELDS}
  ${WEBINAR_LIST_FIELDS}
`;

export const WEBINAR_CONNECTIONS_QUERY = gql`
  query WebinarConnectionsQuery(
    $first: Int!
    $after: String
    $orderBy: String
    $search: String
    $testingServices: String
    $healthAreas: String
    $sampleTypes: String
    $upcoming: Boolean
    $exclude: [ID!]
  ) {
    webinarConnections(
      first: $first
      after: $after
      orderBy: $orderBy
      search: $search
      testingServices: $testingServices
      healthAreas: $healthAreas
      sampleTypes: $sampleTypes
      upcoming: $upcoming
      exclude: $exclude
    ) {
      edges {
        node {
          ...WebinarListFields
        }
        cursor
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
  ${WEBINAR_LIST_FIELDS}
`;

export const UPCOMING_WEBINAR_CONNECTIONS_QUERY = gql`
  query UpcomingWebinarConnectionsQuery($first: Int!, $after: String, $exclude: [ID!]) {
    webinarConnections(
      first: $first
      after: $after
      orderBy: "title"
      upcoming: true
      exclude: $exclude
    ) {
      edges {
        node {
          ...WebinarListFields
        }
        cursor
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
  ${WEBINAR_LIST_FIELDS}
`;

export const SUPPORT_DOCUMENT_CONNECTIONS_QUERY = gql`
  query SupportDocumentConnectionsQuery(
    $first: Int!
    $after: String
    $orderBy: String
    $search: String
  ) {
    documentConnections(first: $first, after: $after, orderBy: $orderBy, search: $search) {
      edges {
        node {
          ...SupportingDocumentFields
        }
        cursor
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
  ${SUPPORTING_DOCUMENT_FIELDS}
`;

export const EVENT_CONNECTIONS_QUERY = gql`
  query EventConnectionsQuery(
    $first: Int!
    $after: String
    $orderBy: String
    $search: String
    $types: [String!]
  ) {
    eventConnections(
      first: $first
      after: $after
      orderBy: $orderBy
      search: $search
      types: $types
    ) {
      edges {
        node {
          ...EventFields
        }
        cursor
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
  ${EVENT_FIELDS}
`;

export const FAQ_FIELDS = gql`
  fragment FaqTypeFields on FaqType {
    id
    question
    answer
  }
`;

export const FAQ_CONNECTIONS_QUERY = gql`
  query FaqConnectionsQuery(
    $first: Int!
    $after: String
    $orderBy: String
    $search: String
    $topics: [ID!]
  ) {
    faqConnections(
      first: $first
      after: $after
      orderBy: $orderBy
      search: $search
      topics: $topics
    ) {
      edges {
        node {
          ...FaqTypeFields
        }
        cursor
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
  ${FAQ_FIELDS}
`;

export const VIDEO_GUIDE_CONNECTIONS_QUERY = gql`
  query VideoGuideConnectionsQuery(
    $first: Int!
    $after: String
    $orderBy: String
    $search: String
    $topics: [ID!]
    $exclude: [ID!]
  ) {
    videoGuideConnections(
      first: $first
      after: $after
      orderBy: $orderBy
      search: $search
      topics: $topics
      exclude: $exclude
    ) {
      edges {
        node {
          ...VideoGuideFields
        }
        cursor
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
  ${VIDEO_GUIDE_FIELDS}
`;

export const VIDEO_GUIDE_QUERY = gql`
  query VideoGuideConnectionsQuery($slug: String!) {
    videoGuide(slug: $slug) {
      ...VideoGuideFields
    }
  }
  ${VIDEO_GUIDE_FIELDS}
`;

const TOPIC_FIELDS = gql`
  fragment TopicFields on TopicType {
    id
    name
  }
`;

export const TOPIC_QUERY = gql`
  query Topics {
    topics {
      ...TopicFields
    }
  }
  ${TOPIC_FIELDS}
`;
